/* ==========================================================================
   Lockett Labs product page, 2026 (assets/ll-product.js)

   Behaviour for sections/static-product.liquid. Everything Liquid knows is
   handed over in the JSON block [data-pdp-variants]; nothing here is
   templated, so the file is cached once for every product page.

   Contents
     1  setup, money, dates
     2  toasts (window.LLToast, shared with the sections below this one)
     3  the human check (hCaptcha), branded, once per session
     4  quantity and purchase limits
     5  variants
     6  adding to the basket: the main button, supplies, rail cards, the swap
     7  release countdown and delivery estimate
     9  card motion (defined before the gallery, which drives it)
     8  gallery, and the viewer (zoom, and inspect for cards)
    10  size guide refit
    11  sticky buy bar
    12  watchlist
    13  rails: bells and arrows
    14  small things: promo code, share, Discord count, market sparkline

   Idle cost: nothing runs on a timer while the page sits still, except the
   card motion, and that only while its card is on screen and the tab is
   visible. The old page redrew the card every frame forever, on screen or
   not, and kept two CSS animations looping as well.
   ========================================================================== */
(function () {
  'use strict';

  var pdp = document.querySelector('[data-llp]');
  if (!pdp) return;
  var root = pdp.closest('.shopify-section') || pdp.parentElement;
  var cfg;
  try { cfg = JSON.parse(root.querySelector('[data-pdp-variants]').textContent); } catch (e) { cfg = { variants: [] }; }
  var P = cfg.product || {};
  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia && matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------------------------------------------------------------- 1 setup */

  /* Land on the product, never mid-page: a stray hash from another page, or
     restored scroll from history, used to open some visits on the FAQ. */
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  if (location.hash) {
    try { history.replaceState(null, '', location.pathname + location.search); } catch (e) {}
    window.scrollTo(0, 0);
  }

  function formatMoney(cents, fmt) {
    fmt = fmt || cfg.money_format || '£{{amount}}';
    function fwd(n, p, t, d) {
      p = p == null ? 2 : p; t = t == null ? ',' : t; d = d == null ? '.' : d;
      if (isNaN(n) || n == null) return '0';
      n = (n / 100).toFixed(p);
      var parts = n.split('.');
      return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t) + (parts[1] ? d + parts[1] : '');
    }
    var re = /\{\{\s*(\w+)\s*\}\}/, m = fmt.match(re), val;
    switch (m && m[1]) {
      case 'amount_no_decimals': val = fwd(cents, 0); break;
      case 'amount_with_comma_separator': val = fwd(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': val = fwd(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': val = fwd(cents, 2, ' ', '.'); break;
      default: val = fwd(cents, 2);
    }
    return String(fmt.replace(re, val)).replace(/<[^>]+>/g, '');
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return '&#' + c.charCodeAt(0) + ';'; }); }
  function $(sel, ctx) { return (ctx || pdp).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || pdp).querySelectorAll(sel)); }
  var ICON = {
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9.5"/><path d="m7.8 12.3 2.8 2.8 5.6-5.8"/></svg>',
    warn: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9.5"/><path d="M12 7.5v5.5M12 16.5h.01"/></svg>',
    bell: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/></svg>',
    x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>',
    prev: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>',
    next: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>',
    zoom: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5M11 8v6M8 11h6"/></svg>',
    card: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="6" y="3" width="12" height="18" rx="2"/><path d="M2 8.5 4 12l-2 3.5M22 8.5 20 12l2 3.5"/></svg>'
  };

  /* ---------------------------------------------------------------- 2 toasts
     One toast at a time, always the latest thing that happened. It pauses
     while the pointer or focus is on it and closes on Escape. Exposed as
     window.LLToast so "You may also like" and recently viewed can report
     here too, instead of each drawing its own. */
  var Toast = (function () {
    var host = null, cur = null, timer = null, left = 0, started = 0;
    function ensure() {
      if (host) return host;
      host = document.createElement('div');
      host.className = 'llp-toasts';
      host.setAttribute('aria-live', 'polite');
      host.setAttribute('aria-atomic', 'true');
      document.body.appendChild(host);
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && cur) close(); });
      return host;
    }
    function close() {
      clearTimeout(timer);
      if (!cur) return;
      var el = cur; cur = null;
      el.classList.remove('is-in');
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 260);
    }
    function arm(ms) {
      clearTimeout(timer);
      left = ms; started = Date.now();
      timer = setTimeout(close, ms);
    }
    function show(html, opts) {
      opts = opts || {};
      ensure();
      if (cur) { var old = cur; cur = null; if (old.parentNode) old.parentNode.removeChild(old); }
      var el = document.createElement('div');
      el.className = 'llp-toast' + (opts.tone ? ' llp-toast--' + opts.tone : '');
      el.setAttribute('role', opts.tone === 'warn' ? 'alert' : 'status');
      var ms = opts.ms || 6000;
      el.innerHTML = html + '<button type="button" class="llp-toast__x" aria-label="Close">' + ICON.x + '</button>' +
        (reduce ? '' : '<span class="llp-toast__timer" style="animation: llp-timer ' + ms + 'ms linear forwards"></span>');
      host.appendChild(el);
      cur = el;
      el.querySelector('.llp-toast__x').addEventListener('click', close);
      function pause() { if (el !== cur) return; clearTimeout(timer); left = Math.max(1200, left - (Date.now() - started)); el.classList.add('is-paused'); }
      function resume() { if (el !== cur) return; el.classList.remove('is-paused'); arm(left); }
      el.addEventListener('mouseenter', pause);
      el.addEventListener('mouseleave', resume);
      el.addEventListener('focusin', pause);
      el.addEventListener('focusout', function (e) { if (!el.contains(e.relatedTarget)) resume(); });
      requestAnimationFrame(function () { requestAnimationFrame(function () { el.classList.add('is-in'); }); });
      arm(ms);
      return el;
    }
    function shipBlock(cart) {
      var th = cfg.free_ship || 0;
      if (!cart || !th) return '';
      var total = cart.total_price || 0;
      if (total >= th) {
        return '<div class="llp-toast__ship is-free"><p>Free UK delivery unlocked on this basket.</p><div class="llp-toast__ship-bar"><i style="width:100%"></i></div></div>';
      }
      var pct = Math.max(4, Math.min(100, Math.round(total / th * 100)));
      return '<div class="llp-toast__ship"><p><b>' + esc(formatMoney(th - total)) + '</b> away from free UK delivery.</p><div class="llp-toast__ship-bar"><i style="width:' + pct + '%"></i></div></div>';
    }
    /* item: { title, image, meta, price } */
    function cart(item, cartObj, opts) {
      opts = opts || {};
      var head = opts.head || 'Added to your basket';
      var count = cartObj && cartObj.item_count;
      var html = '<p class="llp-toast__head">' + ICON.check + '<span>' + esc(head) + '</span></p>' +
        '<div class="llp-toast__body">' +
          (item.image ? '<img class="llp-toast__img" src="' + esc(item.image) + '" alt="" width="56" height="56">' : '<span></span>') +
          '<div><p class="llp-toast__t">' + esc(item.title) + '</p>' +
          '<p class="llp-toast__m">' + (item.meta ? esc(item.meta) + (item.price != null || item.priceText ? ' · ' : '') : '') + (item.price != null ? '<b>' + esc(formatMoney(item.price)) + '</b>' : item.priceText ? '<b>' + esc(item.priceText) + '</b>' : '') + '</p></div>' +
        '</div>' +
        shipBlock(cartObj) +
        '<div class="llp-toast__act"><a class="llp-toast__btn" href="' + esc(cfg.cart_url || '/cart') + '">View basket' + (count ? ' (' + count + ')' : '') + '</a>' +
        '<a class="llp-toast__btn llp-toast__btn--go" href="/checkout">Checkout</a></div>';
      return show(html, { ms: 7000 });
    }
    function watch(on, item) {
      var html = '<p class="llp-toast__head">' + ICON.bell + '<span>' + (on ? 'Watching this for you' : 'Removed from your watchlist') + '</span></p>' +
        '<div class="llp-toast__body">' +
          (item && item.image ? '<img class="llp-toast__img" src="' + esc(item.image) + '" alt="" width="56" height="56">' : '<span></span>') +
          '<div><p class="llp-toast__t">' + esc(item ? item.title : '') + '</p>' +
          '<p class="llp-toast__m">' + (on ? 'We will tell you about restocks, price drops and release news.' : 'You will not get alerts for this any more.') + '</p></div>' +
        '</div>' +
        '<div class="llp-toast__act llp-toast__act--one"><a class="llp-toast__btn" href="' + esc(cfg.watch_url || '/pages/watchlist') + '">View your watchlist</a></div>';
      return show(html, { tone: 'watch', ms: 5000 });
    }
    function msg(text, opts) {
      opts = opts || {};
      var html = '<p class="llp-toast__head">' + (opts.tone === 'warn' ? ICON.warn : ICON.check) + '<span>' + esc(opts.head || (opts.tone === 'warn' ? 'Not added' : 'Done')) + '</span></p>' +
        '<div class="llp-toast__body llp-toast__body--text"><p class="llp-toast__msg">' + esc(text) + '</p></div>' +
        (opts.link ? '<div class="llp-toast__act llp-toast__act--one"><a class="llp-toast__btn" href="' + esc(opts.link.href) + '">' + esc(opts.link.text) + '</a></div>' : '<div style="height:14px"></div>');
      return show(html, { tone: opts.tone, ms: opts.ms || 4500 });
    }
    return { show: show, cart: cart, watch: watch, msg: msg, close: close };
  })();
  window.LLToast = Toast;

  /* ---------------------------------------------------------------- 3 human check
     An hCaptcha solve before anything is added. Shares the session flag
     "cap_atc_ok" with the site-wide gate in theme.liquid, so a shopper solves
     at most one challenge per session whichever fires first.
     Client-side friction only: a direct POST to /cart/add.js bypasses it. The
     limits that cannot be bypassed live at checkout (the quantity-limit
     function) and at login (Shopify's own hCaptcha). */
  var HC_SITEKEY = 'ff761d2e-961a-4122-8f54-be50fe9ee1c8';
  var hc = { id: null, cb: null, el: null, last: null };
  function hcPassed() { try { return sessionStorage.getItem('cap_atc_ok') === '1'; } catch (e) { return false; } }
  function hcLoad(cb) {
    if (window.hcaptcha && window.hcaptcha.render) return cb();
    if (!document.querySelector('script[src*="hcaptcha.com/1/api.js"]')) {
      var sc = document.createElement('script');
      sc.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
      sc.async = true;
      document.head.appendChild(sc);
    }
    var tries = 0;
    var t = setInterval(function () {
      if (window.hcaptcha && window.hcaptcha.render) { clearInterval(t); cb(); }
      else if (++tries > 75) { clearInterval(t); hcHide(); Toast.msg('The quick check could not load. Please try again in a moment.', { tone: 'warn', head: 'Check not loaded' }); }
    }, 200);
  }
  function hcHide() {
    if (!hc.el) return;
    hc.el.hidden = true;
    document.documentElement.classList.remove('llp-lock');
    if (hc.last && hc.last.focus) hc.last.focus();
  }
  function hcModal() {
    if (hc.el) return hc.el;
    var o = document.createElement('div');
    o.className = 'llp-hc';
    o.hidden = true;
    o.setAttribute('role', 'dialog');
    o.setAttribute('aria-modal', 'true');
    o.setAttribute('aria-labelledby', 'llp-hc-title');
    o.innerHTML = '<div class="llp-hc__card">' +
      '<div class="llp-hc__top"><p class="llp-hc__kicker">One moment</p><h2 class="llp-hc__title" id="llp-hc-title">Quick check</h2></div>' +
      '<div class="llp-hc__body"><p>Tick the box so we know you are a collector, not a bot. You will only be asked once this visit.</p>' +
      '<div class="llp-hc__widget" id="llp-hc-widget"></div>' +
      '<button type="button" class="llp-hc__cancel" data-hc-cancel>Not now</button></div></div>';
    document.body.appendChild(o);
    o.querySelector('[data-hc-cancel]').addEventListener('click', function () { hc.cb = null; hcHide(); try { window.hcaptcha.reset(hc.id); } catch (e) {} });
    o.addEventListener('click', function (e) { if (e.target === o) o.querySelector('[data-hc-cancel]').click(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !o.hidden) o.querySelector('[data-hc-cancel]').click(); });
    hc.el = o;
    return o;
  }
  function ensureHuman(cb) {
    if (hcPassed()) return cb();
    hc.cb = cb;
    hc.last = document.activeElement;
    hcLoad(function () {
      var o = hcModal();
      o.hidden = false;
      document.documentElement.classList.add('llp-lock');
      if (hc.id === null) {
        hc.id = window.hcaptcha.render('llp-hc-widget', {
          sitekey: HC_SITEKEY,
          callback: function () {
            try { sessionStorage.setItem('cap_atc_ok', '1'); } catch (e) {}
            hcHide();
            try { window.hcaptcha.reset(hc.id); } catch (e) {}
            if (hc.cb) { var f = hc.cb; hc.cb = null; f(); }
          }
        });
      }
      var cancel = o.querySelector('[data-hc-cancel]');
      if (cancel) cancel.focus({ preventScroll: true });
    });
  }
  window.LLEnsureHuman = window.LLEnsureHuman || ensureHuman;

  /* ---------------------------------------------------------------- 4 quantity
     qMax is what this shopper may still add: -1 = no limit on this product,
     0 = allocation already spent. For guests the ceiling is the raw product
     limit, since their history is unknown until they sign in; the checkout
     function is what holds the line then. */
  var qWrap = $('[data-qty]');
  var plLimit = qWrap ? (parseInt(qWrap.getAttribute('data-limit'), 10) || 0) : 0;
  var plLeft = qWrap ? (parseInt(qWrap.getAttribute('data-limit-left'), 10) || 0) : 0;
  var plKnown = qWrap ? qWrap.getAttribute('data-limit-known') === '1' : false;
  var qMax = plLimit > 0 ? plLeft : -1;
  var qInput = qWrap && qWrap.querySelector('[data-qty-input]');
  function plMessage() {
    if (qMax === 0) return 'You have already had your allocation of ' + plLimit + ' for this drop.';
    if (plKnown && plLeft < plLimit) return 'Limit ' + plLimit + ' per customer. You have ' + plLeft + ' left.';
    return 'Limit ' + plLimit + ' per customer on this drop.';
  }
  function qty() { return Math.max(1, parseInt(qInput ? qInput.value : '1', 10) || 1); }
  if (qWrap && qInput) {
    var qUp = qWrap.querySelector('[data-qty-up]'), qDown = qWrap.querySelector('[data-qty-down]');
    var qSync = function () {
      var n = qty();
      if (qUp) qUp.disabled = qMax >= 0 && n >= qMax;
      if (qDown) qDown.disabled = n <= 1;
    };
    qDown.addEventListener('click', function () { qInput.value = Math.max(1, qty() - 1); qSync(); });
    qUp.addEventListener('click', function () {
      var next = qty() + 1;
      if (qMax >= 0 && next > qMax) { Toast.msg(plMessage(), { tone: 'warn', head: 'Limit reached' }); qSync(); return; }
      qInput.value = next; qSync();
    });
    qInput.addEventListener('change', function () {
      var n = parseInt(qInput.value, 10);
      if (isNaN(n) || n < 1) n = 1;
      if (qMax > 0 && n > qMax) { n = qMax; Toast.msg(plMessage(), { tone: 'warn', head: 'Limit reached' }); }
      qInput.value = n; qSync();
    });
    qSync();
  }

  /* ---------------------------------------------------------------- 5 variants */
  var idInput = $('[data-variant-id]');
  var atc = $('[data-atc]'), atcText = $('[data-atc-text]');
  if (qMax === 0 && atc) {
    atc.disabled = true;
    atc.classList.add('is-out');
    if (atcText) atcText.textContent = 'Limit reached';
  }
  function selectedOptions() {
    var opts = [];
    $$('.llp-opt').forEach(function (g) {
      var sel = g.querySelector('select');
      var val = sel ? sel.value : (g.querySelector('input:checked') || {}).value;
      if (val != null) {
        opts.push(val);
        var c = g.querySelector('[data-opt-current]');
        if (c) c.textContent = val;
      }
    });
    return opts;
  }
  function matchVariant(opts) {
    if (!opts.length) return cfg.variants[0];
    for (var i = 0; i < cfg.variants.length; i++) {
      var v = cfg.variants[i];
      if (v.options.length === opts.length && v.options.every(function (o, j) { return o === opts[j]; })) return v;
    }
    return null;
  }
  function currentVariant() { return matchVariant(selectedOptions()) || cfg.variants[0]; }
  function stockState(v) {
    if (cfg.coming_soon) return { s: 'coming', t: 'Coming soon' };
    if (cfg.preorder) return { s: 'preorder', t: 'Pre-order now' };
    if (!v.available) return { s: 'soldout', t: 'Sold out' };
    if (v.inv_managed && v.inv_qty > 0 && v.inv_qty <= cfg.low_stock) return { s: 'normal', t: 'Only ' + v.inv_qty + ' left', low: true };
    if (v.inv_managed && v.inv_qty > 0) return { s: 'normal', t: 'In stock, ' + v.inv_qty + ' available' };
    return { s: 'normal', t: 'In stock' };
  }
  function updateVariant() {
    var v = matchVariant(selectedOptions());
    if (!v) {
      if (atc) { atc.disabled = true; atc.classList.add('is-out'); }
      if (atcText) atcText.textContent = 'Unavailable';
      return;
    }
    if (idInput) idInput.value = v.id;
    var pending = !v.price;
    var nowEl = $('[data-now]'), wasEl = $('[data-was]'), saveEl = $('[data-save]'), badge = $('[data-sale-badge]');
    if (nowEl) { nowEl.textContent = pending ? 'Price to be confirmed' : formatMoney(v.price); nowEl.classList.toggle('is-pending', pending); }
    var onSale = !pending && v.compare_at_price > v.price;
    if (wasEl) { wasEl.hidden = !onSale; if (onSale) wasEl.textContent = formatMoney(v.compare_at_price); }
    var pct = onSale ? Math.round((v.compare_at_price - v.price) / v.compare_at_price * 100) : 0;
    if (saveEl) { saveEl.hidden = !onSale; if (onSale) saveEl.textContent = 'Save ' + pct + '%'; }
    if (badge) { badge.hidden = !onSale; if (onSale) badge.textContent = '-' + pct + '%'; }
    var st = stockState(v), stockEl = $('[data-stock]');
    if (stockEl) { stockEl.textContent = st.t; stockEl.setAttribute('data-s', st.s); stockEl.classList.toggle('is-low', !!st.low); }
    var blocked = cfg.coming_soon || !v.available || qMax === 0;
    pdp.setAttribute('data-oos', (!cfg.coming_soon && !v.available) ? 'true' : 'false');
    if (atc) {
      atc.classList.toggle('is-out', blocked);
      atc.disabled = blocked;
      if (atcText) atcText.textContent = qMax === 0 ? 'Limit reached' : cfg.coming_soon ? 'Coming soon' : !v.available ? 'Sold out' : cfg.preorder ? 'Pre-order now' : cfg.atc_label;
    }
    var eta = $('[data-delivery-chip]');
    if (eta && !cfg.preorder_mode) eta.hidden = !v.available;
    var barPrice = document.querySelector('[data-bar-price]');
    if (barPrice) barPrice.textContent = pending ? 'Price to be confirmed' : formatMoney(v.price);
    var barState = document.querySelector('[data-bar-state]');
    if (barState) barState.textContent = st.t;
    var barBuy = document.querySelector('[data-sc-bar-buy]');
    if (barBuy) barBuy.disabled = blocked;
    var cond = $('[data-glance-cond]');
    if (cond && v.options) {
      $$('.llp-opt').forEach(function (g, i) {
        var lab = (g.querySelector('.llp-opt__label') || {}).textContent || '';
        if (/condition/i.test(lab) && v.options[i]) cond.textContent = v.options[i];
      });
    }
    try { var u = new URL(location.href); u.searchParams.set('variant', v.id); history.replaceState(history.state, '', u); } catch (e) {}
    Gallery.toMedia(v.media_id);
  }
  $$('.llp-opt input, .llp-opt select').forEach(function (i) { i.addEventListener('change', updateVariant); });

  /* ---------------------------------------------------------------- 6 adding to the basket */
  var Cart = {
    refresh: function () {
      return fetch((cfg.cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.querySelectorAll('[data-cart-count], .cart-count, .cart-link__count').forEach(function (el) { el.textContent = cart.item_count; });
          try {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('ll:cart:updated', { detail: cart }));
          } catch (e) {}
          return cart;
        })
        .catch(function () { return null; });
    },
    add: function (items) {
      return fetch((cfg.cart_add_url || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, d: d }; });
      });
    }
  };
  function busy(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-loading', on);
    if (on) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
    else { btn.removeAttribute('aria-busy'); btn.disabled = btn.classList.contains('is-out'); }
  }
  function failText(res) {
    return (res && res.d && (res.d.description || res.d.message)) || 'Sorry, that could not be added to your basket.';
  }

  /* The supplies panel's running total.
   *
   * Five things can go into the basket from this page in one press, and
   * until now the only way to know what that would cost was to press it.
   * The figure is a polite live region, so it is announced rather than
   * changing silently under a screen reader. Supplies on offer carry the
   * price they were (data-addon-was), so it also says what the ticked ones
   * save. */
  (function supplies() {
    var box = $('[data-addons]');
    var sum = box && $('[data-addon-sum]', box);
    if (!box || !sum) return;
    function paint() {
      var on = $$('[data-addon]:checked', box);
      var total = 0, saved = 0;
      on.forEach(function (a) {
        var p = Number(a.getAttribute('data-addon-price')) || 0;
        var w = Number(a.getAttribute('data-addon-was')) || 0;
        total += p;
        if (w > p) saved += w - p;
      });
      sum.hidden = on.length === 0;
      if (!on.length) { sum.textContent = ''; return; }
      sum.innerHTML = on.length + ' suppl' + (on.length === 1 ? 'y' : 'ies') +
        ' added, <b>+' + formatMoney(total) + '</b>' +
        (saved > 0 ? ', <i>saving ' + formatMoney(saved) + '</i>' : '');
    }
    box.addEventListener('change', paint);
    paint();
  })();

  /* Binder colours (23 Oct 2026).
   *
   * A Vault X binder row offers the binder's other colours as swatches
   * (snippets/ll-pdp-addon.liquid), each colour being its own product. Picking
   * one makes the row that colour: its picture, name, price, "Save n%", and
   * the variant the tick adds. This listener sits on the row, inside the
   * panel, so it runs before the panel's running total reads the price. */
  (function binderColours() {
    $$('[data-addon-set]').forEach(function (set) {
      var tick = $('[data-addon]', set);
      if (!tick) return;
      var row = $('.llp-addon', set);
      var name = $('[data-addon-name]', set);
      var now = $('[data-addon-now]', set);
      var wasLine = $('[data-addon-wasline]', set);
      var wasAmt = $('[data-addon-wasamt]', set);
      var save = $('[data-addon-save]', set);
      var img = $('.llp-addon__img', set);
      var say = $('[data-colour-say]', set);
      function use(r, quiet) {
        var d = r.dataset;
        var price = Number(d.price) || 0, was = Number(d.was) || 0, off = Number(d.off) || 0;
        var onOffer = was > price;
        tick.value = r.value;
        tick.setAttribute('data-addon-title', d.title || '');
        tick.setAttribute('data-addon-price', price);
        if (onOffer) tick.setAttribute('data-addon-was', was); else tick.removeAttribute('data-addon-was');
        if (d.imgSm) tick.setAttribute('data-addon-image', d.imgSm);
        if (row) row.classList.toggle('llp-addon--offer', onOffer);
        if (name) name.textContent = d.name || '';
        if (now) now.textContent = '+' + formatMoney(price);
        if (wasLine) wasLine.hidden = !onOffer;
        if (wasAmt) wasAmt.textContent = formatMoney(was);
        if (save) { save.hidden = off < 10; save.textContent = 'Save ' + off + '%'; }
        if (img && d.img) { img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.src = d.img; }
        if (say && !quiet) say.textContent = (d.colour || '') + ', ' + formatMoney(price) + (onOffer ? ', was ' + formatMoney(was) : '');
      }
      set.addEventListener('change', function (e) {
        var r = e.target;
        if (r && r.type === 'radio' && r.checked && !r.disabled) use(r);
      });
      /* A page restored from history can come back with another colour
         ticked than the one the row was drawn in. */
      var on = $('input[type="radio"]:checked', set);
      if (on && !on.disabled && on.value !== tick.value) {
        use(on, true);
        set.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  })();

  var form = $('[data-llp-form]');
  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!atc || atc.disabled) return;
      ensureHuman(function () {
        var v = currentVariant();
        var props = {};
        $$('input[name^="properties["]', form).forEach(function (i) { props[i.name.slice(11, -1)] = i.value; });
        var items = [{ id: Number(idInput ? idInput.value : v.id), quantity: qty(), properties: props }];
        var addons = $$('[data-addon]:checked', form);
        addons.forEach(function (a) { items.push({ id: Number(a.value), quantity: 1 }); });
        busy(atc, true);
        Cart.add(items).then(function (res) {
          if (!res.ok) { Toast.msg(failText(res), { tone: 'warn' }); return; }
          addons.forEach(function (a) { a.checked = false; });
          /* The panel owns its own total, so tell it the ticks have gone. */
          var sup = $('[data-addons]');
          if (sup) sup.dispatchEvent(new Event('change', { bubbles: true }));
          return Cart.refresh().then(function (cart) {
            var meta = [];
            if (v && v.title && v.title !== 'Default Title') meta.push(v.title);
            if (qty() > 1) meta.push('Qty ' + qty());
            if (addons.length) meta.push('+ ' + addons.length + ' suppl' + (addons.length === 1 ? 'y' : 'ies'));
            Toast.cart({ title: P.title, image: (v && v.image) || P.image, meta: meta.join(', '), price: v ? v.price * qty() : null }, cart,
              { head: cfg.preorder ? 'Pre-order in your basket' : 'Added to your basket' });
          });
        }).catch(function () {
          Toast.msg('We could not reach the basket. Check your connection and try again.', { tone: 'warn' });
        }).then(function () { busy(atc, false); });
      });
    });
  }

  /* Rail cards, the sold-out swap and the set's matching binder
     (snippets/ll-pdp-set-match) post real forms, which work with no script at
     all. With script they go through the same check and land in the same
     toast, and the shopper stays on the page. */
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || !f.matches || !f.matches('.ll-oos__addform, .ll-oos__swap-form, .llp-match__form')) return;
    if (!pdp.contains(f)) return;
    e.preventDefault();
    var btn = f.querySelector('[type="submit"]');
    ensureHuman(function () {
      var id = Number((f.querySelector('[name="id"]') || {}).value);
      if (!id) { f.submit(); return; }
      busy(btn, true);
      Cart.add([{ id: id, quantity: 1 }]).then(function (res) {
        if (!res.ok) { Toast.msg(failText(res), { tone: 'warn' }); return; }
        var card = f.closest('.ll-oos__card, .ll-oos__swap, .llp-match__opt');
        var t = card && card.querySelector('.ll-oos__t, .ll-oos__swap-t, .llp-match__t');
        var img = card && card.querySelector('img');
        /* a matching binder's row says it is in the basket, as it does on the next visit */
        if (card && card.classList.contains('llp-match__opt')) {
          var inB = document.createElement('a');
          inB.className = 'llp-match__add is-in';
          inB.href = cfg.cart_url || '/cart';
          inB.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>In your basket';
          f.replaceWith(inB);
        }
        return Cart.refresh().then(function (cart) {
          Toast.cart({ title: t ? t.textContent.trim() : 'Item', image: img ? img.currentSrc || img.src : '', price: res.d && res.d.items && res.d.items[0] ? res.d.items[0].final_line_price : (res.d && res.d.final_line_price) }, cart);
        });
      }).catch(function () {
        Toast.msg('We could not reach the basket. Check your connection and try again.', { tone: 'warn' });
      }).then(function () { busy(btn, false); });
    });
  }, true);

  /* ---------------------------------------------------------------- 7 dates */
  (function countdown() {
    var el = $('[data-po]');
    if (!el) return;
    var raw = el.getAttribute('data-release');
    var target = raw ? new Date(raw + 'T00:00:00') : null;
    if (!target || isNaN(target)) return;
    var d = el.querySelector('[data-po-d]'), h = el.querySelector('[data-po-h]'), m = el.querySelector('[data-po-m]'), s = el.querySelector('[data-po-s]');
    var title = el.querySelector('[data-po-title]');
    if (!d) return;
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    var timer = null;
    function tick() {
      var diff = target - new Date();
      if (diff <= 0) {
        el.classList.add('is-live');
        if (title) title.textContent = 'Out now';
        stop();
        return;
      }
      var t = Math.floor(diff / 1000);
      d.textContent = Math.floor(t / 86400);
      h.textContent = pad(Math.floor(t % 86400 / 3600));
      m.textContent = pad(Math.floor(t % 3600 / 60));
      s.textContent = pad(t % 60);
    }
    function start() { if (!timer) { tick(); timer = setInterval(tick, 1000); } }
    function stop() { clearInterval(timer); timer = null; }
    tick();
    /* Ticks only while it can be seen: a countdown nobody is looking at does
       not need to repaint every second. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (en) { if (en[0].isIntersecting && !document.hidden) start(); else stop(); }).observe(el);
      document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else { var r = el.getBoundingClientRect(); if (r.bottom > 0 && r.top < innerHeight) start(); } });
    } else { start(); }
  })();

  (function delivery() {
    var dEl = $('[data-delivery]');
    if (!dEl) return;
    var prefix = $('[data-delivery-prefix]');
    var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    function fmt(d) { return DAYS[d.getDay()] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()]; }
    function parseYmd(s) { var m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null; }
    /* Working days: dispatch and delivery both skip the weekend, so an order
       placed on a Friday is not promised for Monday. */
    function addWorking(from, n) {
      var d = new Date(from);
      while (n > 0) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) n--; }
      return d;
    }
    var today = new Date(); today.setHours(0, 0, 0, 0);
    var release = parseYmd(cfg.release_date);
    var isPre = !!(cfg.preorder || cfg.coming_soon || cfg.preorder_mode || cfg.release_future);
    if (isPre && release && release >= today) {
      if (prefix) prefix.textContent = 'Ships on or around';
      dEl.textContent = release.getDate() + ' ' + LONG[release.getMonth()] + ' ' + release.getFullYear();
      return;
    }
    var lead = cfg.dispatch_window == null ? 2 : cfg.dispatch_window;
    var min = addWorking(today, lead + (cfg.ship_min || 2));
    var max = addWorking(today, lead + (cfg.ship_max || 4));
    if (prefix) prefix.textContent = 'Order today, with you';
    dEl.textContent = fmt(min) + ' to ' + fmt(max);
  })();

  /* ---------------------------------------------------------------- 9 card motion
     The gallery photo is a card on white. The card's edges are found once, on
     a small copy of the image, and .llp-card is laid exactly over them with
     the photo cropped to that rectangle and the card's own rounded corners.
     Only that moves; the white around it stays still.

     Motion comes from three sources, eased the same way:
       idle     a slow sway while the card is on screen and nobody is touching
                it, so the foil catches the light by itself
       pointer  on a mouse, the card follows the cursor over it
       inspect  in the viewer, drag (touch) or move (mouse) to tilt
     Everything is written as CSS custom properties on one element, and the
     frame loop stops completely when the card is off screen, the tab is
     hidden, or the card has settled with nothing to do. */
  var Motion = (function () {
    var slide = $('.llp-gal__slide[data-motion]');
    var tier = slide ? slide.getAttribute('data-motion') : '';
    var geo = null, colours = null, ready = false;
    var cards = [];            /* { el, face, target, cur, mode, hover, key } */
    var raf = 0, lastT = 0, inView = false, onSlide = true, paused = false, tabHidden = document.hidden;
    var idleSince = 0;

    function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
    function smallUrl(u, w) {
      if (!u) return u;
      if (/[?&]width=\d+/.test(u)) return u.replace(/([?&]width=)\d+/, '$1' + w);
      return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
    }

    /* the card's rectangle inside the photo, as fractions of the photo */
    function measure(im) {
      var W = 240, cv = document.createElement('canvas');
      var ar = im.naturalWidth / im.naturalHeight || 1;
      cv.width = W; cv.height = Math.round(W / ar);
      var ctx = cv.getContext && cv.getContext('2d', { willReadFrequently: true });
      if (!ctx) return null;
      var data;
      try { ctx.drawImage(im, 0, 0, cv.width, cv.height); data = ctx.getImageData(0, 0, cv.width, cv.height).data; }
      catch (e) { return null; }
      var w = cv.width, h = cv.height;
      function isBg(i) { var a = data[i + 3]; if (a < 24) return true; return data[i] > 238 && data[i + 1] > 238 && data[i + 2] > 238; }
      var rows = new Array(h), cols = new Array(w), x, y;
      for (y = 0; y < h; y++) rows[y] = 0;
      for (x = 0; x < w; x++) cols[x] = 0;
      for (y = 0; y < h; y++) for (x = 0; x < w; x++) if (!isBg((y * w + x) * 4)) { rows[y]++; cols[x]++; }
      var minR = w * 0.06, minC = h * 0.06;
      var y0 = 0, y1 = h - 1, x0 = 0, x1 = w - 1;
      while (y0 < h && rows[y0] < minR) y0++;
      while (y1 > y0 && rows[y1] < minR) y1--;
      while (x0 < w && cols[x0] < minC) x0++;
      while (x1 > x0 && cols[x1] < minC) x1--;
      var bw = (x1 - x0 + 1) / w, bh = (y1 - y0 + 1) / h;
      if (bw < 0.2 || bh < 0.2) return null;
      /* also sample colours for the foil tint while the pixels are here */
      colours = sample(data);
      return { x: x0 / w, y: y0 / h, w: bw, h: bh, ar: (bw * w) / (bh * h) };
    }
    function sample(data) {
      var bins = [], i;
      for (i = 0; i < 12; i++) bins.push({ w: 0, r: 0, g: 0, b: 0 });
      for (i = 0; i < data.length; i += 16) {
        if (data[i + 3] < 125) continue;
        var r = data[i], g = data[i + 1], b = data[i + 2];
        var mx = Math.max(r, g, b) / 255, mn = Math.min(r, g, b) / 255, l = (mx + mn) / 2, d = mx - mn;
        if (!d) continue;
        var s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
        if (s < 0.25 || l < 0.15 || l > 0.88) continue;
        var hh = mx === r / 255 ? ((g - b) / 255 / d + (g < b ? 6 : 0)) : mx === g / 255 ? (b - r) / 255 / d + 2 : (r - g) / 255 / d + 4;
        var bin = Math.floor(hh * 2) % 12, wt = s * (1 - Math.abs(l - 0.55));
        bins[bin].w += wt; bins[bin].r += r * wt; bins[bin].g += g * wt; bins[bin].b += b * wt;
      }
      var used = bins.filter(function (x) { return x.w > 0; }).sort(function (a, b) { return b.w - a.w; }).slice(0, 5);
      if (used.length < 2) return null;
      return used.map(function (x) { return 'rgba(' + Math.round(x.r / x.w) + ',' + Math.round(x.g / x.w) + ',' + Math.round(x.b / x.w) + ',.5)'; });
    }

    /* builds the moving card inside host; box: where it sits, as percentages */
    function makeCard(host, src, box) {
      var el = document.createElement('div');
      el.className = 'llp-card';
      el.setAttribute('data-motion', tier);
      el.setAttribute('aria-hidden', 'true');
      var portrait = geo.ar < 1;
      el.style.setProperty('--cr', portrait ? '4.8% / 3.4%' : '3.4% / 4.8%');
      if (box) {
        el.style.setProperty('--cx', box.x + '%');
        el.style.setProperty('--cy', box.y + '%');
        el.style.setProperty('--cw', box.w + '%');
        el.style.setProperty('--ch', box.h + '%');
      }
      if (colours) colours.forEach(function (c, i) { el.style.setProperty('--foil-c' + (i + 1), c); });
      var face = document.createElement('div');
      face.className = 'llp-card__face';
      /* crop the photo to the card: scale it up by 1/w and slide it by x/(1-w) */
      face.style.backgroundImage = 'url("' + src.replace(/"/g, '%22') + '")';
      face.style.backgroundSize = (100 / geo.w) + '% ' + (100 / geo.h) + '%';
      face.style.backgroundPosition = (geo.w < 1 ? geo.x / (1 - geo.w) * 100 : 0) + '% ' + (geo.h < 1 ? geo.y / (1 - geo.h) * 100 : 0) + '%';
      el.appendChild(face);
      if (tier === 'altart') { var g = document.createElement('span'); g.className = 'llp-card__grain'; face.appendChild(g); }
      host.appendChild(el);
      return el;
    }
    function state() { return { rx: 0, ry: 0, lx: 50, ly: 38, lift: 0, shine: 0 }; }
    function write(c) {
      var s = c.el.style, v = c.cur;
      s.setProperty('--rx', v.rx.toFixed(2) + 'deg');
      s.setProperty('--ry', v.ry.toFixed(2) + 'deg');
      s.setProperty('--lx', v.lx.toFixed(1) + '%');
      s.setProperty('--ly', v.ly.toFixed(1) + '%');
      s.setProperty('--lift', v.lift.toFixed(3));
      s.setProperty('--shine', v.shine.toFixed(3));
    }
    function aim(c, x, y, strength) {
      /* x, y: 0..1 across the card */
      var MX = c.key === 'view' ? 18 : 13, MY = c.key === 'view' ? 16 : 11;
      c.target.ry = (x - 0.5) * 2 * MX * strength;
      c.target.rx = -(y - 0.5) * 2 * MY * strength;
      c.target.lx = x * 100;
      c.target.ly = y * 100;
      c.target.lift = strength;
      c.target.shine = strength;
    }
    function rest(c) { c.target = state(); }

    function frame(t) {
      raf = 0;
      var dt = lastT ? Math.min(0.05, (t - lastT) / 1000) : 0.016;
      lastT = t;
      var busy = false;
      var idleOk = !reduce && !paused && !tabHidden;
      cards.forEach(function (c) {
        if (c.key === 'gal') {
          if (!inView || !onSlide || tabHidden) return;
          if (!c.hover && idleOk && t - idleSince > 900) {
            /* idle sway: slow, a few degrees, the light drifting with it */
            var a = t / 1000;
            var ry = Math.sin(a * 0.62) * 7, rx = Math.sin(a * 0.41 + 1.3) * 3.2;
            c.target.ry = ry; c.target.rx = rx;
            c.target.lx = 50 + ry * 4.2; c.target.ly = 40 - rx * 5;
            c.target.lift = 0.25; c.target.shine = 0.35 + Math.abs(Math.sin(a * 0.62)) * 0.3;
          }
        }
        var k = 1 - Math.exp(-dt * (c.hover ? 14 : 6));
        var moving = false;
        ['rx', 'ry', 'lx', 'ly', 'lift', 'shine'].forEach(function (p) {
          var d = c.target[p] - c.cur[p];
          if (Math.abs(d) > (p === 'lx' || p === 'ly' ? 0.05 : 0.002)) { c.cur[p] += d * k; moving = true; }
        });
        if (moving) {
          busy = true;
          if (c.hover || c.key === 'view' || !c.lastW || t - c.lastW > 31) { write(c); c.lastW = t; }
        }
        if (c.key === 'gal' && idleOk && !c.hover && inView && onSlide) busy = true;
      });
      if (busy) raf = requestAnimationFrame(frame); else lastT = 0;
    }
    function kick() { if (!raf) raf = requestAnimationFrame(frame); }

    function bindPointer(c, surface, mode) {
      function pos(e) {
        var r = c.el.getBoundingClientRect();
        if (!r.width) return null;
        return { x: clamp((e.clientX - r.left) / r.width, 0, 1), y: clamp((e.clientY - r.top) / r.height, 0, 1) };
      }
      if (mode === 'hover') {
        surface.addEventListener('pointerenter', function (e) { if (e.pointerType !== 'mouse') return; c.hover = true; kick(); });
        surface.addEventListener('pointermove', function (e) { if (e.pointerType !== 'mouse') return; var p = pos(e); if (!p) return; c.hover = true; aim(c, p.x, p.y, 1); kick(); }, { passive: true });
        surface.addEventListener('pointerleave', function (e) { if (e.pointerType !== 'mouse') return; c.hover = false; rest(c); idleSince = performance.now(); kick(); });
      } else {
        var down = false;
        surface.addEventListener('pointerdown', function (e) { down = true; c.hover = true; try { surface.setPointerCapture(e.pointerId); } catch (x) {} var p = pos(e); if (p) aim(c, p.x, p.y, 1); kick(); });
        surface.addEventListener('pointermove', function (e) {
          if (!down && e.pointerType !== 'mouse') return;
          var p = pos(e); if (!p) return;
          c.hover = true; aim(c, p.x, p.y, 1); kick();
        }, { passive: true });
        var up = function () { down = false; c.hover = false; rest(c); kick(); };
        surface.addEventListener('pointerup', up);
        surface.addEventListener('pointercancel', up);
        surface.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') up(); });
      }
    }

    function init() {
      if (!slide) return;
      var img = slide.querySelector('img');
      if (!img) return;
      var probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.decoding = 'async';
      probe.onload = function () {
        geo = measure(probe);
        if (!geo) return;
        /* A card is taller than wide at about 0.72, or wider at about 1.39.
           Anything else is not a card scan, so it is left still rather than
           tilting a shape that is not a card. */
        var okShape = (geo.ar > 0.6 && geo.ar < 0.84) || (geo.ar > 1.2 && geo.ar < 1.66);
        if (!okShape) return;
        ready = true;
        var src = img.currentSrc || img.src;
        /* the photo is square and fills the square slide, so fractions of
           the photo are fractions of the slide */
        var box = { x: geo.x * 100, y: geo.y * 100, w: geo.w * 100, h: geo.h * 100 };
        var el = makeCard(slide, src, box);
        slide.classList.add('has-card');
        var c = { el: el, target: state(), cur: state(), hover: false, key: 'gal' };
        cards.push(c);
        write(c);
        if (fine) bindPointer(c, slide, 'hover');
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (en) { inView = en[0].isIntersecting; if (inView) { idleSince = performance.now(); kick(); } }, { threshold: 0.25 }).observe(slide);
        } else { inView = true; }
        document.addEventListener('visibilitychange', function () { tabHidden = document.hidden; if (!tabHidden) kick(); });
        idleSince = performance.now();
        kick();
      };
      probe.src = smallUrl(img.currentSrc || img.src, 480);
    }
    function start() {
      if (!slide) return;
      var img = slide.querySelector('img');
      if (img && !img.complete) img.addEventListener('load', init, { once: true });
      else init();
    }
    if (slide) {
      if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 1500 }); else setTimeout(start, 300);
    }

    return {
      canInspect: function () { return ready; },
      visible: function (on) { onSlide = on; if (on) { idleSince = performance.now(); kick(); } },
      pause: function (on) { paused = on; if (!on) kick(); },
      inspect: function (host, s) {
        if (!ready) return;
        host.style.setProperty('--car', geo.ar);
        var full = s.getAttribute('data-full') || (s.querySelector('img') || {}).src;
        var el = makeCard(host, full, null);
        var c = { el: el, target: state(), cur: state(), hover: false, key: 'view' };
        cards.push(c);
        write(c);
        bindPointer(c, host, fine ? 'hover' : 'drag');
        /* a small welcome tilt so it is obvious the card moves */
        if (!reduce) {
          aim(c, 0.72, 0.3, 0.8); kick();
          setTimeout(function () { if (!c.hover) { rest(c); kick(); } }, 700);
        }
      },
      release: function (key) {
        cards = cards.filter(function (c) { if (c.key === key) { if (c.el.parentNode) c.el.parentNode.removeChild(c.el); return false; } return true; });
      }
    };
  })();

  /* ---------------------------------------------------------------- 8 gallery */
  var Gallery = (function () {
    var rail = $('[data-sc-rail]');
    var slides = rail ? $$('.llp-gal__slide', rail) : [];
    var thumbs = $$('[data-sc-dot]');
    var countI = $('[data-gal-i]');
    var prev = $('[data-sc-prev]'), next = $('[data-sc-next]');
    var cur = 0;
    function index() { return rail ? Math.min(slides.length - 1, Math.max(0, Math.round(rail.scrollLeft / Math.max(1, rail.clientWidth)))) : 0; }
    function mark(i) {
      cur = i;
      thumbs.forEach(function (t, j) { t.classList.toggle('is-active', j === i); if (j === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
      if (countI) countI.textContent = i + 1;
      if (prev) prev.disabled = i <= 0;
      if (next) next.disabled = i >= slides.length - 1;
      Motion.visible(i === 0);
    }
    function go(i, instant) {
      if (!rail) return;
      i = Math.max(0, Math.min(slides.length - 1, i));
      rail.scrollTo({ left: i * rail.clientWidth, behavior: reduce || instant ? 'auto' : 'smooth' });
      mark(i);
    }
    if (rail) {
      var st = null;
      rail.addEventListener('scroll', function () { clearTimeout(st); st = setTimeout(function () { var i = index(); if (i !== cur) mark(i); }, 70); }, { passive: true });
      if (prev) prev.addEventListener('click', function () { go(index() - 1); });
      if (next) next.addEventListener('click', function () { go(index() + 1); });
      thumbs.forEach(function (t, i) { t.addEventListener('click', function () { go(i); }); });
      slides.forEach(function (sl, i) {
        sl.addEventListener('click', function () { Viewer.open(i); });
        sl.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); Viewer.open(i); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); go(i + 1); if (slides[i + 1]) slides[i + 1].focus({ preventScroll: true }); }
          else if (e.key === 'ArrowLeft') { e.preventDefault(); go(i - 1); if (slides[i - 1]) slides[i - 1].focus({ preventScroll: true }); }
        });
      });
      var openBtn = $('[data-gal-open]');
      if (openBtn) openBtn.addEventListener('click', function (e) { e.stopPropagation(); Viewer.open(index()); });
      mark(0);
    }
    function toMedia(id) {
      if (!id) return;
      for (var i = 0; i < slides.length; i++) if (String(slides[i].getAttribute('data-sc-media')) === String(id)) { go(i); return; }
    }
    return { slides: slides, go: go, toMedia: toMedia, index: index };
  })();

  /* The viewer: every image full screen, arrows, thumbnails, keyboard, and a
     zoom that follows the pointer. A card's first image opens in inspect
     mode instead: the card alone, large, tilting under a finger or a mouse,
     which is how a collector checks a holo in their hand. */
  var Viewer = (function () {
    var el = null, stage, count, thumbsEl, img, cardHost, zoomBtn, idx = 0, last = null, mode = 'image';
    var z = 1, px = 0, py = 0, drag = null;
    var slides = Gallery.slides;
    function build() {
      if (el) return;
      el = document.createElement('div');
      el.className = 'llp-view';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Images of ' + (P.title || 'this product'));
      el.innerHTML =
        '<div class="llp-view__top"><span class="llp-view__title">' + esc(P.title || '') + '</span>' +
        '<span class="llp-view__count" data-v-count></span>' +
        '<button type="button" class="llp-view__btn" data-v-zoom aria-pressed="false" aria-label="Zoom">' + ICON.zoom + '</button>' +
        '<button type="button" class="llp-view__btn" data-v-close aria-label="Close">' + ICON.x + '</button></div>' +
        '<div class="llp-view__stage" data-v-stage>' +
        (slides.length > 1 ? '<button type="button" class="llp-view__btn llp-view__arrow llp-view__arrow--prev" data-v-prev aria-label="Previous image">' + ICON.prev + '</button><button type="button" class="llp-view__btn llp-view__arrow llp-view__arrow--next" data-v-next aria-label="Next image">' + ICON.next + '</button>' : '') +
        '</div>' +
        '<p class="llp-view__foot" data-v-foot></p>' +
        (slides.length > 1 ? '<div class="llp-view__thumbs" data-v-thumbs></div>' : '');
      document.body.appendChild(el);
      stage = el.querySelector('[data-v-stage]');
      count = el.querySelector('[data-v-count]');
      zoomBtn = el.querySelector('[data-v-zoom]');
      thumbsEl = el.querySelector('[data-v-thumbs]');
      if (thumbsEl) {
        slides.forEach(function (s, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', 'Image ' + (i + 1));
          var si = s.querySelector('img');
          b.innerHTML = si ? '<img src="' + esc(si.currentSrc || si.src) + '" alt="">' : '';
          b.addEventListener('click', function () { show(i); });
          thumbsEl.appendChild(b);
        });
      }
      el.querySelector('[data-v-close]').addEventListener('click', close);
      zoomBtn.addEventListener('click', function () {
        if (mode === 'card') { show(idx, 'image'); setZoom(2.2, 0, 0); return; }
        setZoom(z > 1 ? 1 : 2.2, 0, 0);
      });
      var p = el.querySelector('[data-v-prev]'), n = el.querySelector('[data-v-next]');
      if (p) p.addEventListener('click', function () { show(idx - 1); });
      if (n) n.addEventListener('click', function () { show(idx + 1); });
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { e.preventDefault(); close(); }
        else if (e.key === 'ArrowRight') show(idx + 1);
        else if (e.key === 'ArrowLeft') show(idx - 1);
        else if (e.key === 'Tab') {
          var f = $$('button, a[href]', el).filter(function (b) { return b.offsetParent !== null; });
          if (!f.length) return;
          if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
          else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
        }
      });
      /* swipe between images when not zoomed and not inspecting */
      var sx = null;
      stage.addEventListener('pointerdown', function (e) { if (mode === 'image' && z === 1 && e.pointerType !== 'mouse') sx = e.clientX; });
      stage.addEventListener('pointerup', function (e) {
        if (sx === null) return;
        var dx = e.clientX - sx; sx = null;
        if (Math.abs(dx) > 50) show(idx + (dx < 0 ? 1 : -1));
      });
    }
    function setZoom(nz, cx, cy) {
      z = nz; px = cx || 0; py = cy || 0;
      if (!img) return;
      img.style.setProperty('--z', z);
      img.style.setProperty('--px', px + 'px');
      img.style.setProperty('--py', py + 'px');
      el.classList.toggle('is-zoomed', z > 1);
      zoomBtn.setAttribute('aria-pressed', z > 1 ? 'true' : 'false');
    }
    function show(i, force) {
      if (!slides.length) return;
      idx = (i + slides.length) % slides.length;
      var s = slides[idx];
      var motion = s.getAttribute('data-motion');
      mode = force || (motion && idx === 0 ? 'card' : 'image');
      $$('img.llp-view__img, .llp-view__card', stage).forEach(function (n) { n.parentNode.removeChild(n); });
      Motion.release('view');
      img = null; cardHost = null; z = 1;
      el.classList.remove('is-zoomed');
      zoomBtn.setAttribute('aria-pressed', 'false');
      var foot = el.querySelector('[data-v-foot]');
      if (mode === 'card' && Motion.canInspect()) {
        cardHost = document.createElement('div');
        cardHost.className = 'llp-view__card';
        stage.insertBefore(cardHost, stage.firstChild);
        Motion.inspect(cardHost, s);
        zoomBtn.setAttribute('aria-label', 'Zoom into the image');
        if (foot) foot.textContent = fine ? 'Move the mouse over the card to tilt it. Zoom for a closer look at the surface.' : 'Drag across the card to tilt it. Zoom for a closer look at the surface.';
      } else {
        img = document.createElement('img');
        img.className = 'llp-view__img';
        img.alt = (s.querySelector('img') || {}).alt || '';
        img.src = s.getAttribute('data-full') || ((s.querySelector('img') || {}).src || '');
        img.draggable = false;
        stage.insertBefore(img, stage.firstChild);
        zoomBtn.setAttribute('aria-label', 'Zoom');
        if (foot) foot.textContent = fine ? 'Click to zoom, then move to look around.' : 'Tap to zoom, then drag to look around.';
        bindZoom(img);
      }
      if (count) count.textContent = slides.length > 1 ? (idx + 1) + ' / ' + slides.length : '';
      if (thumbsEl) $$('button', thumbsEl).forEach(function (b, j) { if (j === idx) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current'); });
    }
    function bindZoom(im) {
      im.addEventListener('click', function (e) {
        if (drag && drag.moved) return;
        if (z > 1) { setZoom(1); return; }
        var r = im.getBoundingClientRect();
        var cx = (r.left + r.width / 2 - e.clientX) * 1.2, cy = (r.top + r.height / 2 - e.clientY) * 1.2;
        setZoom(2.2, cx, cy);
      });
      im.addEventListener('pointerdown', function (e) {
        if (z === 1) return;
        drag = { x: e.clientX, y: e.clientY, px: px, py: py, moved: false };
        el.classList.add('is-dragging');
        try { im.setPointerCapture(e.pointerId); } catch (x) {}
      });
      im.addEventListener('pointermove', function (e) {
        if (z > 1 && fine && !drag) {
          var r = im.getBoundingClientRect();
          var nx = (e.clientX - (r.left + r.width / 2 - px)) / (r.width / z);
          var ny = (e.clientY - (r.top + r.height / 2 - py)) / (r.height / z);
          px = -nx * (r.width / z) * (z - 1) * 0.9; py = -ny * (r.height / z) * (z - 1) * 0.9;
          im.style.setProperty('--px', px + 'px'); im.style.setProperty('--py', py + 'px');
          return;
        }
        if (!drag) return;
        var dx = e.clientX - drag.x, dy = e.clientY - drag.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) drag.moved = true;
        px = drag.px + dx; py = drag.py + dy;
        im.style.setProperty('--px', px + 'px'); im.style.setProperty('--py', py + 'px');
      });
      function end() { el.classList.remove('is-dragging'); setTimeout(function () { drag = null; }, 0); }
      im.addEventListener('pointerup', end);
      im.addEventListener('pointercancel', end);
    }
    function open(i) {
      if (!slides.length) return;
      build();
      last = document.activeElement;
      document.documentElement.classList.add('llp-lock');
      el.hidden = false;
      show(i || 0);
      requestAnimationFrame(function () { el.classList.add('is-in'); });
      el.querySelector('[data-v-close]').focus({ preventScroll: true });
      Motion.pause(true);
    }
    function close() {
      if (!el || el.hidden) return;
      Motion.release('view');
      el.classList.remove('is-in');
      document.documentElement.classList.remove('llp-lock');
      setTimeout(function () { el.hidden = true; }, reduce ? 0 : 200);
      Motion.pause(false);
      if (last && last.focus) last.focus({ preventScroll: true });
    }
    return { open: open, close: close };
  })();

  /* ---------------------------------------------------------------- 10 size guide
     The product photo sits in white space, so "contain" would draw the plush
     smaller than its stated height. Its outline is found the same way as a
     card's, and the photo is refitted so the plush itself spans the height. */
  (function sizeGuide() {
    var fig = $('[data-llp-scale]');
    if (!fig) return;
    var box = fig.querySelector('[data-scale-item]');
    var img = box && box.querySelector('img');
    if (!img) return;
    function run() {
      var probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = function () {
        var W = 160, cv = document.createElement('canvas');
        var ar = probe.naturalWidth / probe.naturalHeight || 1;
        cv.width = W; cv.height = Math.round(W / ar);
        var ctx = cv.getContext('2d', { willReadFrequently: true }), data;
        try { ctx.drawImage(probe, 0, 0, cv.width, cv.height); data = ctx.getImageData(0, 0, cv.width, cv.height).data; } catch (e) { return; }
        var w = cv.width, h = cv.height, x0 = w, x1 = -1, y0 = h, y1 = -1;
        for (var y = 0; y < h; y++) for (var x = 0; x < w; x++) {
          var i = (y * w + x) * 4;
          if (data[i + 3] > 24 && !(data[i] > 240 && data[i + 1] > 240 && data[i + 2] > 240)) {
            if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
          }
        }
        if (x1 < 0) return;
        var fw = (x1 - x0 + 1) / w, fh = (y1 - y0 + 1) / h;
        if (fw < 0.15 || fh < 0.15) return;
        box.style.setProperty('--item-ar', ((x1 - x0 + 1) / (y1 - y0 + 1)).toFixed(3));
        box.style.overflow = 'hidden';
        img.style.position = 'absolute';
        img.style.maxWidth = 'none';
        img.style.objectFit = 'fill';
        img.style.width = (100 / fw) + '%';
        img.style.height = (100 / fh) + '%';
        img.style.left = (-x0 / w / fw * 100) + '%';
        img.style.top = (-y0 / h / fh * 100) + '%';
        box.classList.add('is-fit');
      };
      probe.src = img.currentSrc || img.src;
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (en) { if (en[0].isIntersecting) { io.disconnect(); run(); } }, { rootMargin: '300px' });
      io.observe(fig);
    } else run();
  })();

  /* ---------------------------------------------------------------- 11 sticky buy bar
     One bar on every screen size: thumbnail, name, price, bell and the main
     action. It follows the real button: up once that has scrolled away, down
     while it is on screen. The old phone layout stacked a second identity
     strip over it; that strip is folded in here. It is moved to <body> so no
     later section can paint over it, and keeps .ll-pdp__bar and data-on so
     ll-mobile-app.js still publishes its height for the tab bar, the
     watchlist tab and the footer to stack against. */
  (function bar() {
    var b = root.querySelector('[data-sc-bar]');
    if (!b) return;
    try { if (b.parentNode !== document.body) document.body.appendChild(b); } catch (e) {}
    var anchor = function () {
      var a = $('.llp-form__buy');
      if (a && a.offsetParent !== null) return a;
      a = $('.ll-oos__swap') || $('.llp-alert') || $('.llp__title-row');
      return a;
    };
    var last = null;
    function sync() {
      var a = anchor();
      var on = false;
      if (a) { var r = a.getBoundingClientRect(); on = r.bottom < 0; }
      var foot = document.querySelector('.llft, footer');
      if (on && foot) { var fr = foot.getBoundingClientRect(); if (fr.top < innerHeight * 0.55) on = false; }
      if (on === last) return;
      last = on;
      b.setAttribute('data-on', on ? 'true' : 'false');
      b.setAttribute('aria-hidden', on ? 'false' : 'true');
      $$('a, button', b).forEach(function (x) { x.tabIndex = on ? 0 : -1; });
    }
    var ticking = false;
    window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(function () { ticking = false; sync(); }); } }, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    sync();
    var buy = b.querySelector('[data-sc-bar-buy]');
    if (buy) buy.addEventListener('click', function () {
      if (!form || !atc || atc.disabled) return;
      if (form.requestSubmit) form.requestSubmit(atc); else atc.click();
    });
    var w = b.querySelector('[data-bar-watch]');
    var pw = $('[data-pdp-watch]');
    if (w && pw) {
      var wl = w.querySelector('[data-bar-watch-label]');
      var wl0 = wl ? wl.textContent : '';
      var syncW = function () {
        /* read the list itself: this runs before the page's bells repaint */
        var on = readWatch().indexOf(P.handle) >= 0;
        w.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (wl) wl.textContent = on ? 'Watching' : wl0;
      };
      w.addEventListener('click', function () { pw.click(); syncW(); });
      document.addEventListener('ll:watchlist:change', syncW);
      syncW();
    } else if (w) { w.hidden = true; }
  })();

  /* ---------------------------------------------------------------- 12 watchlist
     Same storage as the rest of the site: ll_watchlist (array of handles,
     capped at 100) mirrored to ll_preorder_watch, a snapshot per handle in
     ll_watchlist_snap, and an ll:watchlist:change event. */
  var WKEY = 'll_watchlist', WLEG = 'll_preorder_watch';
  function readWatch() {
    try {
      var l = JSON.parse(localStorage.getItem(WKEY) || 'null');
      if (Object.prototype.toString.call(l) === '[object Array]') return l;
      var old = JSON.parse(localStorage.getItem(WLEG) || '[]') || [];
      if (Object.prototype.toString.call(old) === '[object Array]' && old.length) { writeWatch(old); return old; }
    } catch (e) {}
    return [];
  }
  function writeWatch(list) {
    var clean = [];
    (list || []).forEach(function (h) { if (h && clean.indexOf(h) < 0) clean.push(h); });
    clean = clean.slice(0, 100);
    try { localStorage.setItem(WKEY, JSON.stringify(clean)); localStorage.setItem(WLEG, JSON.stringify(clean)); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('ll:watchlist:change', { detail: { list: clean } })); } catch (e) {}
    try { if (window.LLWatchlistLive && window.LLWatchlistLive.refresh) window.LLWatchlistLive.refresh(); } catch (e) {}
    return clean;
  }
  (function watch() {
    var btns = $$('[data-pdp-watch]');
    if (!btns.length || !P.handle) return;
    function sync() {
      var on = readWatch().indexOf(P.handle) >= 0;
      btns.forEach(function (b) {
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.setAttribute('aria-label', on ? 'Stop watching ' + P.title : 'Watch ' + P.title + ' for alerts');
        b.title = on ? 'Watching. Tap to stop.' : 'Watch for alerts';
        var lab = b.querySelector('[data-watch-label]');
        if (lab) {
          if (!lab.hasAttribute('data-off')) lab.setAttribute('data-off', lab.textContent);
          lab.textContent = on ? 'Watching' : lab.getAttribute('data-off');
        }
      });
    }
    function toggle() {
      var list = readWatch(), i = list.indexOf(P.handle), on;
      var snaps;
      try { snaps = JSON.parse(localStorage.getItem('ll_watchlist_snap') || '{}') || {}; } catch (e) { snaps = {}; }
      if (i >= 0) {
        list.splice(i, 1); on = false;
        delete snaps[P.handle];
        try { if (window.LLWatchlistLive && window.LLWatchlistLive.stop) window.LLWatchlistLive.stop(P.handle); } catch (e) {}
      } else {
        list.push(P.handle); on = true;
        var v = currentVariant() || {};
        snaps[P.handle] = { handle: P.handle, title: P.title, url: P.url, image: P.image, price: v.price, available: !!v.available };
      }
      try { localStorage.setItem('ll_watchlist_snap', JSON.stringify(snaps)); } catch (e) {}
      writeWatch(list);
      sync();
      Toast.watch(on, { title: P.title, image: P.image });
    }
    btns.forEach(function (b) { b.addEventListener('click', toggle); });
    document.addEventListener('ll:watchlist:change', sync);
    sync();
  })();

  /* ---------------------------------------------------------------- 13 rails */
  (function rails() {
    var hosts = $$('.ll-oos');
    if (!hosts.length) return;
    function paint() {
      var on = readWatch();
      $$('[data-oos-watch]').forEach(function (b) {
        var w = on.indexOf(b.getAttribute('data-handle')) >= 0;
        b.setAttribute('aria-pressed', w ? 'true' : 'false');
        b.title = w ? 'Watching. Tap to stop.' : 'Watch for alerts';
      });
    }
    pdp.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-oos-watch]') : null;
      if (!b) return;
      e.preventDefault();
      var h = b.getAttribute('data-handle');
      if (!h) return;
      var list = readWatch(), i = list.indexOf(h), on = i < 0;
      if (on) list.push(h); else list.splice(i, 1);
      writeWatch(list);
      paint();
      var card = b.closest('.ll-oos__card');
      var t = card && card.querySelector('.ll-oos__t'), im = card && card.querySelector('img');
      Toast.watch(on, { title: t ? t.textContent.trim() : '', image: im ? im.currentSrc || im.src : '' });
    });
    document.addEventListener('ll:watchlist:change', paint);
    paint();
    var groups = $$('.ll-oos__group');
    hosts.forEach(function (h) { if (h.classList.contains('ll-oos--related')) groups.push(h); });
    groups.forEach(function (group) {
      var track = group.querySelector('[data-oos-track]');
      var nav = group.querySelector('.ll-oos__nav');
      if (!track || !nav) return;
      var prev = nav.querySelector('[data-oos-prev]'), next = nav.querySelector('[data-oos-next]');
      var rail = track.parentElement;
      function page() {
        var card = track.firstElementChild;
        var step = card ? card.getBoundingClientRect().width + 12 : 180;
        return Math.max(step, Math.floor(track.clientWidth / step) * step);
      }
      function sync() {
        var max = track.scrollWidth - track.clientWidth;
        nav.setAttribute('data-static', max > 4 ? '0' : '1');
        var l = track.scrollLeft > 2, r = max > 4 && track.scrollLeft < max - 2;
        if (rail) rail.setAttribute('data-more', l && r ? 'both' : l ? 'left' : r ? 'right' : 'none');
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
      }
      if (prev) prev.addEventListener('click', function () { track.scrollLeft -= page(); });
      if (next) next.addEventListener('click', function () { track.scrollLeft += page(); });
      track.addEventListener('scroll', sync, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(sync).observe(track); else window.addEventListener('resize', sync);
      sync();
    });
  })();

  /* Add a drop's release day to a calendar: an all-day event in a small
     .ics file, made here, so no calendar service sees the visit. */
  (function calendar() {
    var btn = $('[data-po-cal]');
    var el = $('[data-po]');
    if (!btn || !el) return;
    var m = String(el.getAttribute('data-release') || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) { btn.hidden = true; return; }
    function icsText(t) { return String(t).replace(/\\/g, '\\\\').replace(/([,;])/g, '\\$1').replace(/\n/g, '\\n'); }
    function ymd(d) { return d.getFullYear() + ('0' + (d.getMonth() + 1)).slice(-2) + ('0' + d.getDate()).slice(-2); }
    btn.addEventListener('click', function () {
      var start = new Date(+m[1], +m[2] - 1, +m[3]);
      var end = new Date(start); end.setDate(end.getDate() + 1);
      var now = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z');
      var title = btn.getAttribute('data-title') || document.title;
      var url = btn.getAttribute('data-url') || location.href;
      var ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Lockett Labs//Release day//EN', 'CALSCALE:GREGORIAN', 'BEGIN:VEVENT',
        'UID:' + (P.handle || 'release') + '-release@lockett-labs.com', 'DTSTAMP:' + now,
        'DTSTART;VALUE=DATE:' + ymd(start), 'DTEND;VALUE=DATE:' + ymd(end),
        'SUMMARY:' + icsText(title + ' releases'),
        'DESCRIPTION:' + icsText('Release day. Pre-orders and stock at Lockett Labs: ' + url),
        'URL:' + url, 'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
      var a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
      a.download = (P.handle || 'release') + '-release-day.ics';
      document.body.appendChild(a); a.click();
      setTimeout(function () { URL.revokeObjectURL(a.href); a.parentNode.removeChild(a); }, 1000);
    });
  })();

  /* WHAT'S IN THE BOX. Sealed listings usually carry the contents as a list
     under a line like "Elite Trainer Box includes:". That list is the most
     useful thing on the page for a sealed buyer and sat at the bottom of the
     description, so it is repeated beside the price. Only a real list of two
     or more items after such a line counts; nothing is written up. */
  (function contents() {
    var box = $('[data-llp-box]'), out = $('[data-llp-box-list]');
    if (!box || !out) return;
    var rte = $$('.llp-sec--about .llp-rte');
    var found = null;
    rte.forEach(function (r) {
      if (found) return;
      $$('p, h2, h3, h4, h5, h6, strong, b, div', r).some(function (n) {
        var t = (n.textContent || '').replace(/\s+/g, ' ').trim();
        if (t.length > 90 || !/(includes|contains|contents|in the box|inside the box|each box|what(?:'|’)?s inside)\s*:?$/i.test(t)) return false;
        var nx = n;
        while (nx && !nx.nextElementSibling && nx.parentElement && nx.parentElement !== r) nx = nx.parentElement;
        nx = nx && nx.nextElementSibling;
        if (nx && /^(UL|OL)$/.test(nx.tagName) && nx.children.length >= 2) { found = nx; return true; }
        return false;
      });
    });
    if (!found) return;
    $$('li', found).slice(0, 16).forEach(function (li) {
      var t = (li.textContent || '').replace(/\s+/g, ' ').trim();
      if (!t) return;
      var item = document.createElement('li');
      var mm = t.match(/^(\d+\s*x?)\s+(.*)$/i);
      if (mm) { var bb = document.createElement('b'); bb.textContent = mm[1].replace(/\s*x$/i, ''); item.appendChild(bb); item.appendChild(document.createTextNode(' ' + mm[2])); }
      else item.textContent = t;
      out.appendChild(item);
    });
    if (out.children.length >= 2) box.hidden = false;
  })();

  /* Many listings open with a pasted "Product Description" heading, which
     only repeats the section title above it. */
  $$('.llp-rte').forEach(function (r) {
    var first = r.firstElementChild;
    if (first && /^(H[1-6]|P|STRONG|B)$/.test(first.tagName) && /^\s*product description\s*:?\s*$/i.test(first.textContent)) first.hidden = true;
  });

  /* ---------------------------------------------------------------- 14 small things */
  function copyText(text, done) {
    function legacy() {
      var ta = document.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.top = '-1000px';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(ta); done();
    }
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, legacy); else legacy();
  }
  $$('[data-copy-code]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      copyText(btn.getAttribute('data-copy-code') || '', function () {
        btn.classList.add('is-copied');
        setTimeout(function () { btn.classList.remove('is-copied'); }, 1600);
      });
    });
  });
  (function share() {
    var box = $('[data-share]');
    if (!box) return;
    var nat = box.querySelector('[data-share-native]');
    if (nat && navigator.share) {
      nat.hidden = false;
      nat.addEventListener('click', function () { navigator.share({ title: nat.getAttribute('data-title') || document.title, url: nat.getAttribute('data-url') || location.href }).catch(function () {}); });
    }
    var cp = box.querySelector('[data-share-copy]');
    if (cp) {
      var lab = cp.querySelector('[data-share-copy-label]'), orig = lab ? lab.textContent : '';
      cp.addEventListener('click', function () {
        copyText(cp.getAttribute('data-url') || location.href, function () {
          cp.classList.add('is-copied');
          if (lab) lab.textContent = 'Link copied';
          setTimeout(function () { cp.classList.remove('is-copied'); if (lab) lab.textContent = orig; }, 1800);
        });
      });
    }
  })();

  /* The Discord online count: fetched only when a card that shows it comes
     near the screen, cached for ten minutes, and shown only as a real number. */
  (function discord() {
    var cards = $$('[data-ll3-discord]');
    if (!cards.length || !window.fetch) return;
    var id = cards[0].getAttribute('data-ll3-server-id') || '1483863211873800262';
    var KEY = 'll_disc_online_' + id;
    function paint(n) {
      if (typeof n !== 'number' || !(n > 0)) return;
      cards.forEach(function (c) {
        c.querySelectorAll('[data-ll3-online]').forEach(function (b) { b.textContent = n.toLocaleString('en-GB') + (b.closest('.llp-alert__online') ? ' members online' : ''); });
        var wrap = c.querySelector('[data-ll3-online-wrap]');
        if (wrap) wrap.hidden = false;
      });
    }
    function load() {
      try { var hit = JSON.parse(sessionStorage.getItem(KEY) || 'null'); if (hit && Date.now() - hit.t < 600000) { paint(hit.n); return; } } catch (e) {}
      fetch('https://discord.com/api/guilds/' + id + '/widget.json')
        .then(function (r) { if (!r.ok) throw new Error('off'); return r.json(); })
        .then(function (d) { var n = d && d.presence_count; try { sessionStorage.setItem(KEY, JSON.stringify({ n: n, t: Date.now() })); } catch (e) {} paint(n); })
        .catch(function () {});
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (en) { if (en.some(function (x) { return x.isIntersecting; })) { io.disconnect(); load(); } }, { rootMargin: '400px' });
      cards.forEach(function (c) { io.observe(c); });
    } else load();
  })();

  (function sparkline() {
    var box = $('.ll-mkt__chart[data-hist]');
    if (!box) return;
    var raw;
    try { raw = JSON.parse(box.getAttribute('data-hist')); } catch (e) { box.hidden = true; return; }
    var pts = (raw || []).map(function (x) { return typeof x === 'number' ? x : (x && (x.p != null ? x.p : x.price)); }).filter(function (n) { return typeof n === 'number' && !isNaN(n); });
    if (pts.length < 2) { box.hidden = true; return; }
    var min = Math.min.apply(null, pts), max = Math.max.apply(null, pts), rng = (max - min) || 1, W = 300, H = 54, pad = 5, n = pts.length;
    var d = pts.map(function (p, i) { return (i ? 'L' : 'M') + (pad + (W - 2 * pad) * i / (n - 1)).toFixed(1) + ' ' + (pad + (H - 2 * pad) * (1 - (p - min) / rng)).toFixed(1); }).join(' ');
    var col = pts[n - 1] >= pts[0] ? '#0a7a44' : '#c2410c';
    box.querySelector('svg').innerHTML = '<path d="' + d + '" fill="none" stroke="' + col + '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>';
  })();
})();
